import type { SerializedEditorState } from 'lexical';

import StoreORM from 'js/src/data/store-orm';
import { createEditorFromEditorState } from 'js/src/data/transcript/transcript-operations';
import type {
    LexicalEditorsDataType,
    SerializedTranscript,
} from 'js/src/data/transcript/transcript-types';
import type { GetRootState, RootState } from 'js/src/libs/RootState';

/** The saved transcripts a fetched video ships, keyed by scene-group id (video.transcripts). */
type SavedTranscripts = Record<string, Partial<SerializedTranscript> | SerializedEditorState>;

/**
 * Marks a state as describing a reference video rather than the one being edited. For the editing
 * video a missing editor means hydration failed and must throw; a reference video may legitimately
 * ship no saved transcripts, so it falls back to the persisted words instead.
 */
export const REFERENCE_CAPTURE = Symbol.for('agent.referenceCapture');

type ReferenceState = RootState & { [REFERENCE_CAPTURE]?: true };

export function isReferenceCapture(state: RootState | undefined): boolean {
    return Boolean((state as ReferenceState | undefined)?.[REFERENCE_CAPTURE]);
}

/**
 * A reference video is loaded into an unmounted Luminary, so the session store has no transcript
 * editors for it — which means its pauses (SilenceNodes, read from the editor) can't be seen.
 *
 * Build headless Lexical editors from the video's saved transcript states (the same
 * createEditorFromEditorState the editor hydrates with) and overlay them onto the session state's
 * lexical_editors slice, producing a getState the capture pipeline can run. This makes a reference
 * video's transcript render through the exact same code as get_video_state — per-scene `covers:`
 * slices with pauses — rather than a separate, lesser view.
 *
 * The session state is spread, never mutated: StoreORM's Immutable persistent ops leave the live
 * lexical_editors untouched, and everything else the capture reads (notably the templates slice
 * that isSceneAnOverlay needs to classify overlays) is preserved as-is from the session state.
 *
 * A reference with no transcripts still returns a getter carrying the session state, since the
 * capture also resolves color grading and the template's named palettes against the store.
 */
export function buildReferenceStateGetter(
    sessionState: RootState,
    transcripts: SavedTranscripts | undefined,
): GetRootState {
    const editors: LexicalEditorsDataType[] = [];
    for (const sceneGroupId of Object.keys(transcripts ?? {})) {
        const transcript = transcripts![sceneGroupId] as Partial<SerializedTranscript>;
        // A saved transcript is either a SerializedTranscript (carrying .editorState) or the raw
        // editor state itself — the same two shapes hydrateTranscripts accepts.
        const editorState = (transcript?.editorState ?? transcripts![sceneGroupId]) as
            | SerializedEditorState
            | undefined;
        if (!editorState) {
            continue;
        }
        editors.push({
            editor: createEditorFromEditorState(editorState),
            sceneGroupId,
            lexicalControlsItsOwnState: false,
        });
    }

    // Drop the session's own editors before adding the reference's: they belong to the video being
    // edited, so a scene-group id shared with the reference would describe the wrong transcript.
    // With no reference editors to add this just leaves the slice empty, and the groups fall back
    // to their persisted words.
    const orm = new StoreORM<LexicalEditorsDataType, string>(
        sessionState.transcript.lexical_editors,
    );
    orm.delete(orm.getAllIds());
    editors.forEach((editor) => orm.pushJSItem(editor, 'sceneGroupId'));

    const referenceState: ReferenceState = {
        ...sessionState,
        transcript: { ...sessionState.transcript, lexical_editors: orm.data },
        [REFERENCE_CAPTURE]: true,
    };
    return () => referenceState;
}
